import React, { CSSProperties, useEffect, useMemo, useState } from 'react'

import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating
} from '@floating-ui/react'
import { FloatingPortalToRoot } from '@inno-ui/z-index'
import cn from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import { useHotkeys } from 'react-hotkeys-hook'

import styles from './HotkeyTooltip.module.scss'

type Keys = 'mod' | 'shift' | 'alt' | 'ctrl' | 'meta' | 'option' | string

type HotkeyTooltipProps = {
  keys?: Keys[]
  onPress: () => void
  label?: React.ReactNode
  className?: string
  tooltipClassName?: string
  children: React.ReactNode
  maxTooltipWidth?: CSSProperties['maxWidth']
  previewHotkeysOnPress?: boolean
}

const isMacLike = () => {
  if (typeof navigator === 'undefined') return false
  const platform = (
    navigator.platform ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigator as any).userAgentData?.platform ||
    ''
  ).toLowerCase()
  return (
    platform.includes('mac') ||
    platform.includes('iphone') ||
    platform.includes('ipad')
  )
}

const normalizeKeyForBinding = (key: string): string => {
  const k = key.toLowerCase()
  const mac = isMacLike()

  if (mac) {
    if (k === 'command' || k === 'cmd' || k === 'meta') return 'mod'
    if (k === 'option') return 'alt'
    if (k === 'ctrl') return 'control'
    return k
  }

  if (k === 'command' || k === 'cmd' || k === 'meta') return 'mod'
  if (k === 'option') return 'alt'
  if (k === 'ctrl') return 'control'
  return k
}

const isModifierKey = (k: string): boolean => {
  const key = k.toLowerCase()
  return (
    key === 'mod' ||
    key === 'ctrl' ||
    key === 'meta' ||
    key === 'shift' ||
    key === 'alt' ||
    key === 'option'
  )
}

const toDisplayKey = (key: string, mac: boolean): string => {
  const k = key.toLowerCase()
  if (
    k === 'mod' ||
    k === 'command' ||
    k === 'cmd' ||
    k === 'meta' ||
    k === 'ctrl'
  ) {
    return mac ? '⌘' : 'Ctrl'
  }
  if (k === 'alt' || k === 'option') {
    return mac ? '⌥' : 'Alt'
  }
  if (k === 'shift') {
    return mac ? '⇧' : 'Shift'
  }
  return key.length === 1 ? key.toUpperCase() : key
}

export const HotkeyTooltip: React.FC<HotkeyTooltipProps> = ({
  keys,
  onPress,
  label,
  className,
  tooltipClassName,
  children,
  maxTooltipWidth,
  previewHotkeysOnPress = true
}) => {
  keys = keys?.filter(Boolean) ?? []

  const [isHovered, setIsHovered] = useState(false)
  const [isModifierPressed, setIsModifierPressed] = useState(false)

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [offset(5), flip(), shift({ padding: 10 })]
  })

  const mac = isMacLike()

  // Build binding hotkey like "mod+shift+t"
  const { hotkeyForBinding, modifiersInCombo, displayKeys } = useMemo(() => {
    const normalized = keys.map(normalizeKeyForBinding)
    const deduped = Array.from(new Set(normalized))

    const modifiers = deduped.filter(isModifierKey)
    const nonModifiers = deduped.filter(k => !isModifierKey(k))

    // ensure order: mod, shift, alt, then others
    const ordered = [
      ...['mod'].filter(m => modifiers.includes(m)),
      ...['shift'].filter(m => modifiers.includes(m)),
      ...['alt'].filter(m => modifiers.includes(m)),
      ...nonModifiers
    ]

    const binding = ordered.join('+')
    const display = keys.map(k => toDisplayKey(k, mac))

    return {
      hotkeyForBinding: binding,
      modifiersInCombo: new Set(
        modifiers.includes('mod') ? ['mod', 'meta', 'ctrl'] : modifiers
      ),
      displayKeys: display
    }
  }, [keys, mac])

  useHotkeys(
    hotkeyForBinding,
    e => {
      e.preventDefault()
      onPress()
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
      enabled: keys.length > 0
    },
    [hotkeyForBinding, onPress]
  )

  useEffect(() => {
    if (!keys.length) return

    const handleKeyChange = (e: KeyboardEvent) => {
      const modPressed = mac ? e.metaKey : e.ctrlKey
      setIsModifierPressed(modPressed)
    }

    const handleWindowBlur = () => {
      setIsModifierPressed(false)
    }

    window.addEventListener('blur', handleWindowBlur)
    window.addEventListener('keydown', handleKeyChange)
    window.addEventListener('keyup', handleKeyChange)
    return () => {
      window.removeEventListener('blur', handleWindowBlur)
      window.removeEventListener('keydown', handleKeyChange)
      window.removeEventListener('keyup', handleKeyChange)
    }
  }, [modifiersInCombo, mac, previewHotkeysOnPress, keys.length])

  const showTooltip = isHovered || (isModifierPressed && previewHotkeysOnPress)

  return (
    <div
      className={cn(styles.wrapper, className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div ref={refs.setReference}>{children}</div>

      <FloatingPortalToRoot type='floatingUI'>
        <AnimatePresence>
          {showTooltip && (
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              className={cn(styles.tooltip, tooltipClassName, {
                [styles.hovered]: isHovered
              })}
            >
              <motion.div
                className={styles.content}
                style={{ maxWidth: maxTooltipWidth }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
              >
                {isHovered && label ? <span>{label}</span> : null}
                {isModifierPressed && (
                  <div className={styles.kbdWrapper}>
                    {displayKeys.map((k, i) => (
                      <React.Fragment key={`${k}-${i}`}>
                        {i > 0 ? <span> + </span> : null}
                        <span className={styles.kbd}>{k}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </FloatingPortalToRoot>
    </div>
  )
}
